import React, { useContext, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Utils
import { AuthContext } from "../context/AuthContext";
import { useForm } from "../hooks/useForm";
import toastConfig from "../utils/toastConfig";

// Assets
import "../assets/css/LoginPage.css";
import logo from "../assets/images/logo-dark.png";
import bot from "../assets/images/bot.png";

// Constants
const baseUrl = process.env.REACT_APP_API;

/**
 * Página de inicio de sesión
 *
 * @returns {JSX.Element} Componente de React
 */
export const LoginPage = () => {
  const bgRef = useRef(null);

  // Contexto de autenticación
  const { login } = useContext(AuthContext);

  // Custom hook para el manejo de formularios
  const [values, handleInputChange, setFormValue] = useForm({
    user: "",
    password: "",
    rememberme: false,
  });

  // Efecto para recordar el usuario
  useEffect(() => {
    setUser();
  }, []);

  /**
   * Función para recordar el usuario
   *
   * @returns {void}
   */
  const setUser = () => {
    const user = localStorage.getItem("user");
    user && setFormValue((values) => ({ ...values, user, rememberme: true }));
  };

  /**
   * Función para cambiar el estado del checkbox de recordar usuario
   *
   * @returns {void}
   */
  const toggleCheck = () => {
    setFormValue({
      ...values,
      rememberme: !values.rememberme,
    });
  };

  /**
   * Función para enviar el formulario
   *
   * @param {Event} ev - Evento del formulario
   * @returns {void}
   */
  const onSubmit = async (ev) => {
    ev.preventDefault();

    values.rememberme
      ? localStorage.setItem("user", values.user)
      : localStorage.removeItem("user");

    const { user, password } = values;

    const ok = await login(user, password);

    if (!ok)
      toast.error(
        "¡Error, verifica que tus datos sean correctos!",
        toastConfig
      );
  };

  /**
   * Función para verificar que los campos no estén vacíos
   *
   * @returns {boolean} Verdadero si los campos no están vacíos
   */
  const fieldsCheck = () =>
    values.user.length > 0 && values.password.length > 0;

  // Efecto para obtener la imagen de fondo
  useEffect(() => {
    const getData = async () => {
      const bgContainer = bgRef.current;
      const background = localStorage.getItem("bg-login");

      background
        ? (bgContainer.style.backgroundImage = `url(${baseUrl}/${background})`)
        : (bgContainer.style.backgroundImage = `url("https://getwallpapers.com/wallpaper/full/0/4/5/45698.jpg")`);
    };

    getData();
  }, []);

  return (
    <div
      id="loginPage"
      ref={bgRef}
      className="container-fluid vh-100 d-flex justify-content-center align-items-center login-container"
    >
      <div className="col-7">
        <div className="row rounded login">
          <div className="col-5 col-lg-4 d-none d-md-flex flex-column justify-content-evenly align-items-center gap-2">
            <span className="h2 text-login">¡Bienvenido!</span>

            <img src={bot} alt="rwt" className="w-50" />

            <span className="h1 text-login">SysChat</span>
          </div>

          <div className="col-12 col-md-7 col-lg-8">
            <div className="d-flex d-md-none justify-content-center mb-3">
              <h2 className="h2 text-login">¡Bienvenido!</h2>
            </div>

            <div>
              <form onSubmit={onSubmit}>
                <div className="d-flex flex-column justify-content-evenly gap-3">
                  <div className="d-flex flex-column gap-3">
                    <input
                      type="text"
                      name="user"
                      placeholder="Usuario"
                      id="inputUser"
                      value={values.user}
                      onChange={handleInputChange}
                      className="input-login"
                    />

                    <input
                      type="password"
                      name="password"
                      placeholder="Contraseña"
                      id="inputPassword"
                      onChange={handleInputChange}
                      className="input-login"
                    />

                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="checkRemember"
                        name="rememberme"
                        className="form-check-input"
                        onChange={toggleCheck}
                      />
                      <label
                        className="form-check-label text-login"
                        htmlFor="checkRemember"
                      >
                        Recordar usuario
                      </label>
                    </div>
                  </div>

                  <div className="row d-flex justify-content-evenly align-items-center">
                    <button
                      type="submit"
                      disabled={!fieldsCheck()}
                      className="my-2 col-8 col-md-4 button-login"
                    >
                      Ingresar
                    </button>

                    <img
                      src={logo}
                      alt="rwt"
                      className="d-none d-md-block col-6 h-75"
                    />
                  </div>

                  <div className="d-flex justify-content-end d-md-none">
                    <img src={logo} alt="rwt" className="col-5" />
                  </div>
                </div>

                <ToastContainer />
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
